// Field definitions are read from the catalog templates, never duplicated here.
export const cloneConfiguration = value => JSON.parse(JSON.stringify(value));
export const controlSelector = 'input, select, textarea';

export function dimensionNumber(value) {
  const text = String(value ?? '').trim().replace(',', '.');
  return text === '' ? null : Number.isFinite(Number(text)) ? Number(text) : null;
}

export function addDimensionOffset(value, offset) {
  const number = dimensionNumber(value);
  const addition = dimensionNumber(offset);
  return number === null || addition === null ? null : number + addition;
}

export function describeMultiPositionSteps(steps, extension = {}) {
  const templates = new Map(steps.map(step => {
    const template = document.createElement('template');
    template.innerHTML = step.html || '';
    return [String(step.id), template.content];
  }));
  const positionStepIds = new Set(steps.filter(step => {
    const root = templates.get(String(step.id));
    return root.querySelector('input.x_value, input.y_value, input[class*="_b_value"], input[class*="_h_value"], [data-position-specific]')
      || (extension.positionSelector && root.querySelector(extension.positionSelector));
  }).map(step => String(step.id)));

  // Conditions on a position field make the entire dependent step position-specific.
  let changed = true;
  while (changed) {
    changed = false;
    steps.forEach(step => {
      if (!positionStepIds.has(String(step.id)) && (step.rules || []).some(rule =>
        positionStepIds.has(String(rule.inhalt || '').split(';')[0]))) {
        positionStepIds.add(String(step.id));
        changed = true;
      }
    });
  }

  const fields = [];
  const errors = [];
  steps.forEach(step => {
    if ((step.rules || []).some(rule => ![0, 2, 3].includes(Number(rule.bool_type)))) {
      errors.push(`Die Regeln für „${step.name}“ werden in der Multiposition nicht unterstützt.`);
    }
    if (!positionStepIds.has(String(step.id)) || Number(step.type) === 3) return;
    const root = templates.get(String(step.id));
    Array.from(root.querySelectorAll(controlSelector)).forEach((control, index) => {
      if (control.type === 'hidden') return;
      if (control.tagName === 'INPUT' && !['text', 'number', 'checkbox'].includes(control.type)) {
        errors.push(`Das Feld „${step.name}“ kann nicht zuverlässig in der Tabelle erfasst werden.`);
        return;
      }
      const container = control.closest('.input-container') || control.parentElement;
      const label = (container?.querySelector('.container-input-label')?.textContent
        || control.getAttribute('aria-label') || step.name).replace(/:\s*$/, '').trim();
      const numeric = control.type === 'number' || control.matches('input.x_value, input.y_value, input[class*="_b_value"], input[class*="_h_value"]');
      const field = {
        key: `${step.id}:${index}`, stepId: String(step.id), index, label,
        type: control.tagName === 'SELECT' ? 'select' : control.type === 'checkbox' ? 'checkbox' : 'text',
        numeric, required: control.classList.contains('mandatory-field') || !!Number(step.is_mandatory),
        readOnly: control.disabled || control.readOnly,
        placeholder: control.getAttribute('placeholder') || '',
        unit: container?.querySelector('.unit')?.textContent.replace(/[()]/g, '').trim() || '',
        options: Array.from(control.options || []).map(option => ({
          value: option.value, label: option.textContent, disabled: option.disabled,
        })),
        initial: {
          value: control.value, checked: control.checked || false, disabled: control.disabled,
          selectedIndex: control.selectedIndex, dataset: { ...control.dataset }, style: control.style.cssText,
        },
      };
      fields.push({ ...field, ...extension.describeField?.({ field, control, step }) });
    });
  });

  if (!fields.length) errors.push('Es konnten keine positionsabhängigen Eingabefelder ermittelt werden. Bitte die Einzelposition verwenden.');
  const schema = { fields, positionStepIds: [...positionStepIds], calculations: [], errors };
  extension.extendSchema?.({ schema, steps, templates });
  return schema;
}

export function updateMultiPositionField(row, field, value, schema) {
  const change = field.type === 'select' && value && typeof value === 'object' ? value
    : { [field.type === 'checkbox' ? 'checked' : 'value']: value };
  row.fields[field.key] = { ...row.fields[field.key], ...change };
  for (const calculation of schema.calculations) {
    if (!calculation.sourceKeys.includes(field.key)) continue;
    const calculated = calculation.calculate(row.fields);
    if (Number.isFinite(calculated)) row.fields[calculation.targetKey] = {
      ...row.fields[calculation.targetKey], value: String(calculated),
    };
  }
}

export function getMultiPositionFieldWarning(row, field, schema) {
  const calculation = schema.calculations.find(candidate => candidate.targetKey === field.key && candidate.warnOnOverride);
  if (!calculation) return '';
  const standard = calculation.calculate(row.fields);
  const actual = dimensionNumber(row.fields[field.key]?.value);
  return Number.isFinite(standard) && actual !== null && Math.abs(standard - actual) > 0.0001
    ? `Standard: ${standard}${field.unit ? ` ${field.unit}` : ''}` : '';
}

// Canonical comparison also accepts JSON whose object keys were reordered by a server.
export function samePositionPayload(position, payload) {
  const canonical = value => {
    if (Array.isArray(value)) return value.map(canonical);
    if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().map(key => [key, canonical(value[key])]));
    return value;
  };
  try {
    return Number(position.anzahl) === Number(payload.anzahl)
      && String(position.position_bezeichnung || '') === String(payload.position_bezeichnung || '')
      && String(position.notizen || '') === String(payload.notizen || '')
      && JSON.stringify(canonical(JSON.parse(position.artikel_json))) === JSON.stringify(canonical(JSON.parse(payload.artikel_json)));
  } catch {
    return false;
  }
}
